import { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Download, Copy, FileDown, Loader2, Sparkles, Instagram,
  Image as ImageIcon, CheckCircle2, ExternalLink, ArrowUp, Minus, TrendingUp, Quote, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PALETTE BRAND
const COL = {
  bg: "#0F1F14", card: "#163321", accent: "#D97B58", warn: "#B84A3C",
  highlight: "#E5C97B", muted: "#7A8B82", light: "#FCFCFA", lightMuted: "#4A5E51",
};

function StemmaColucci({ inverted = false, size = 14 }) {
  const color = inverted ? COL.bg : "#FFF";
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size }}>
      <path d="M12 1 L22 7 L22 17 L12 23 L2 17 L2 7 Z" fill="none" stroke={color} strokeWidth="1.8" />
      <path d="M12 6 L17 9 L17 15 L12 18 L7 15 L7 9 Z" fill={color} />
      <text x="12" y="15" textAnchor="middle" fontSize="6.5" fontWeight="900" fill={inverted ? "#FFF" : COL.bg}>C</text>
    </svg>
  );
}

// SLIDE FRAMES optimized for 1080x1350 rendering (export)
// scale=1 means natural IG size, scale=0.28 for preview
function ExportFrame({ index, total, children, bg = COL.bg, light = false, scale = 1, handle = "@colucci_assicuratori", domain = "colucciassicuratori.com" }) {
  const textColor = light ? COL.bg : "#FFF";
  const mutedColor = light ? COL.lightMuted : COL.muted;
  const W = 1080 * scale, H = 1350 * scale;
  return (
    <div className="relative overflow-hidden"
      style={{
        width: W, height: H, backgroundColor: bg, color: textColor,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
      {/* grid */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.06,
          backgroundImage: `linear-gradient(${light ? "rgba(15,31,20,0.5)" : "rgba(255,255,255,0.5)"} 1px, transparent 1px), linear-gradient(90deg, ${light ? "rgba(15,31,20,0.5)" : "rgba(255,255,255,0.5)"} 1px, transparent 1px)`,
          backgroundSize: `${60 * scale}px ${60 * scale}px`,
        }} />
      {/* glow */}
      <div className="absolute rounded-full"
        style={{
          top: -60 * scale, right: -60 * scale,
          width: 520 * scale, height: 520 * scale,
          background: COL.accent, opacity: 0.10,
          filter: `blur(${120 * scale}px)`,
        }} />

      {/* top bar */}
      <div className="absolute flex items-center justify-between"
        style={{ top: 36 * scale, left: 48 * scale, right: 48 * scale }}>
        <div className="flex items-center" style={{ gap: 8 * scale }}>
          <StemmaColucci inverted={light} size={22 * scale} />
          <span style={{
            fontSize: 14 * scale, fontWeight: 700,
            letterSpacing: "0.25em", textTransform: "uppercase",
            color: mutedColor,
          }}>
            Colucci Assicuratori
          </span>
        </div>
        <span style={{
          fontSize: 14 * scale, fontFamily: "ui-monospace, monospace",
          fontWeight: 600, color: mutedColor,
        }}>
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>

      {/* bottom bar */}
      <div className="absolute flex items-center justify-between"
        style={{ bottom: 36 * scale, left: 48 * scale, right: 48 * scale }}>
        <span style={{
          fontSize: 13 * scale, fontFamily: "ui-monospace, monospace", fontWeight: 600,
          letterSpacing: "0.15em", textTransform: "uppercase", color: mutedColor,
        }}>{handle}</span>
        <span style={{ fontSize: 13 * scale, fontFamily: "ui-monospace, monospace", color: mutedColor }}>
          {domain}
        </span>
      </div>

      <div className="absolute"
        style={{ top: 110 * scale, left: 56 * scale, right: 56 * scale, bottom: 90 * scale }}>
        {children(scale)}
      </div>
    </div>
  );
}

// ===== SLIDE CONTENTS (parametric) =====
function SlideHook({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div>
        <div style={{
          display: "inline-block", padding: `${6 * scale}px ${16 * scale}px`, borderRadius: 4 * scale,
          background: COL.highlight, color: COL.bg,
          fontSize: 14 * scale, fontWeight: 900, letterSpacing: "0.25em", textTransform: "uppercase",
          marginBottom: 32 * scale,
        }}>{data.badge}</div>
        <h2 style={{
          fontWeight: 900, fontSize: 120 * scale, lineHeight: 0.9,
          letterSpacing: "-0.03em", margin: 0, color: "#FFF",
        }}>
          {data.titleLines.map((ln, i) => (
            <div key={i} style={ln.italic ? { fontStyle: "italic", fontWeight: 300, color: COL.muted } : {}}>{ln.text}</div>
          ))}
        </h2>
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 * scale, marginBottom: 8 * scale }}>
          <ArrowUp style={{ width: 80 * scale, height: 80 * scale, color: COL.accent, strokeWidth: 3, marginBottom: 8 * scale }} />
          <span style={{
            fontWeight: 900, fontSize: 260 * scale, lineHeight: 1,
            color: COL.accent, letterSpacing: "-0.04em", fontVariantNumeric: "tabular-nums",
          }}>
            {data.metricMain}<span style={{ fontSize: 140 * scale }}>{data.metricUnit}</span>
          </span>
        </div>
        <p style={{
          fontSize: 20 * scale, fontFamily: "ui-monospace, monospace",
          letterSpacing: "0.2em", textTransform: "uppercase", color: COL.muted, margin: 0,
        }}>{data.source}</p>
      </div>
    </div>
  );
}

function SlideBarchart({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <h2 style={{ fontWeight: 900, fontSize: 72 * scale, lineHeight: 0.95, margin: 0, letterSpacing: "-0.02em", color: "#FFF" }}>
        {data.topTitle}
      </h2>
      <h2 style={{ fontWeight: 900, fontSize: 160 * scale, lineHeight: 0.9, margin: `${12 * scale}px 0 ${40 * scale}px`, color: COL.accent, letterSpacing: "-0.04em" }}>
        {data.bigTitle}
      </h2>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 20 * scale, flex: 1, paddingBottom: 20 * scale }}>
        {data.bars.map((d) => (
          <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 * scale, justifyContent: "flex-end" }}>
            <span style={{ fontSize: 20 * scale, fontFamily: "ui-monospace, monospace", fontWeight: 700, color: d.highlight ? COL.accent : COL.muted }}>
              {d.top || ""}
            </span>
            <div style={{
              width: "100%", borderRadius: `${8 * scale}px ${8 * scale}px 0 0`,
              height: `${(d.value / 100) * 100}%`,
              background: d.highlight ? COL.accent : COL.card,
              border: d.highlight ? "none" : `1px solid ${COL.muted}33`,
            }} />
            <span style={{ fontSize: 22 * scale, fontFamily: "ui-monospace, monospace", color: d.highlight ? "#FFF" : COL.muted }}>
              {d.label}
            </span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 18 * scale, fontFamily: "ui-monospace, monospace", letterSpacing: "0.2em", textTransform: "uppercase", color: COL.muted, margin: 0 }}>
        {data.source}
      </p>
    </div>
  );
}

function SlideKPI({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <h2 style={{ fontWeight: 900, fontSize: 80 * scale, lineHeight: 0.95, margin: 0, letterSpacing: "-0.02em", color: "#FFF" }}>
        Lo scenario<br /><span style={{ color: COL.muted }}>in tre numeri.</span>
      </h2>
      <div style={{ marginTop: 40 * scale, display: "flex", flexDirection: "column", gap: 20 * scale, flex: 1 }}>
        {data.kpis.map((s, i) => (
          <div key={i} style={{
            padding: 28 * scale, borderRadius: 16 * scale,
            background: COL.card, border: "1px solid #ffffff15",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <span style={{ fontWeight: 900, fontSize: 84 * scale, lineHeight: 1, color: "#FFF", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", display: "block" }}>
                {s.value}
              </span>
              <span style={{ fontSize: 18 * scale, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginTop: 6 * scale, color: COL.muted, display: "block" }}>
                {s.label}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 * scale }}>
              {s.trend === "up"
                ? <ArrowUp style={{ width: 32 * scale, height: 32 * scale, color: COL.warn, strokeWidth: 3 }} />
                : <Minus style={{ width: 32 * scale, height: 32 * scale, color: COL.muted, strokeWidth: 3 }} />}
              {s.delta && <span style={{ fontSize: 20 * scale, fontFamily: "ui-monospace, monospace", fontWeight: 700, color: s.trend === "up" ? COL.warn : COL.muted }}>{s.delta}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideWarning({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{
        alignSelf: "flex-start", display: "inline-block",
        padding: `${6 * scale}px ${16 * scale}px`, borderRadius: 4 * scale,
        background: COL.warn, color: "#FFF",
        fontSize: 14 * scale, fontWeight: 900, letterSpacing: "0.25em", textTransform: "uppercase",
        marginBottom: 24 * scale,
      }}>WARNING</div>
      <h2 style={{ fontWeight: 900, fontSize: 86 * scale, lineHeight: 0.95, margin: `0 0 ${40 * scale}px`, letterSpacing: "-0.02em", color: "#FFF" }}>
        Cosa la polizza<br /><span style={{ color: COL.warn }}>NON</span>&nbsp;copre.
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 28 * scale, flex: 1 }}>
        {data.items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 24 * scale, alignItems: "flex-start" }}>
            <span style={{ fontWeight: 900, fontSize: 56 * scale, lineHeight: 1, width: 80 * scale, color: COL.warn, fontVariantNumeric: "tabular-nums" }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <div style={{ borderLeft: `${3 * scale}px solid ${COL.warn}55`, paddingLeft: 20 * scale }}>
              <p style={{ fontWeight: 700, fontSize: 34 * scale, color: "#FFF", margin: 0, lineHeight: 1.1 }}>{it.headline}</p>
              <p style={{ fontSize: 22 * scale, color: COL.muted, margin: `${6 * scale}px 0 0` }}>{it.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideChecklist({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 * scale, marginBottom: 20 * scale }}>
        <TrendingUp style={{ width: 32 * scale, height: 32 * scale, color: COL.accent, strokeWidth: 3 }} />
        <span style={{ fontSize: 16 * scale, fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: COL.accent }}>
          Insurance · Checklist
        </span>
      </div>
      <h2 style={{ fontWeight: 900, fontSize: 84 * scale, lineHeight: 0.95, margin: `0 0 ${40 * scale}px`, letterSpacing: "-0.02em", color: COL.bg }}>
        Prima del rinnovo.<br /><span style={{ color: COL.lightMuted }}>3 domande.</span>
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 * scale, flex: 1 }}>
        {data.checks.map((t, i) => (
          <div key={i} style={{ display: "flex", gap: 20 * scale, alignItems: "flex-start" }}>
            <div style={{
              width: 56 * scale, height: 56 * scale, borderRadius: 12 * scale,
              border: `${3 * scale}px solid ${COL.accent}`, background: COL.accent + "22",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <span style={{ fontWeight: 900, fontSize: 28 * scale, color: COL.accent }}>{i + 1}</span>
            </div>
            <p style={{ fontSize: 32 * scale, fontWeight: 600, color: COL.bg, margin: 0, paddingTop: 10 * scale, lineHeight: 1.2 }}>{t}</p>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 16 * scale, fontFamily: "ui-monospace, monospace", letterSpacing: "0.2em", textTransform: "uppercase", color: COL.lightMuted, margin: `${20 * scale}px 0 0` }}>
        Non sostituisce consulenza IVASS personalizzata.
      </p>
    </div>
  );
}

function SlideQuote({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div>
        <Quote style={{ width: 80 * scale, height: 80 * scale, color: COL.accent, strokeWidth: 2, marginBottom: 24 * scale }} />
        <p style={{ fontWeight: 900, fontSize: 66 * scale, lineHeight: 1.1, letterSpacing: "-0.02em", color: "#FFF", margin: 0, whiteSpace: "pre-line" }}>
          <span>&ldquo;</span>{data.quoteLine1}<br />
          <span style={{ color: COL.accent }}>{data.quoteLine2}</span>
          <span style={{ color: COL.accent }}>&rdquo;</span>
        </p>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 20 * scale, paddingTop: 32 * scale, borderTop: `1px solid #ffffff25` }}>
        <div style={{ width: 88 * scale, height: 88 * scale, borderRadius: "50%", overflow: "hidden", background: COL.card, flexShrink: 0 }}>
          <img src={data.authorPhoto} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div>
          <p style={{ fontSize: 28 * scale, fontWeight: 700, color: "#FFF", margin: 0, lineHeight: 1.1 }}>{data.authorName}</p>
          <p style={{ fontSize: 16 * scale, fontFamily: "ui-monospace, monospace", letterSpacing: "0.2em", textTransform: "uppercase", color: COL.muted, margin: `${6 * scale}px 0 0` }}>
            {data.authorRole}
          </p>
        </div>
      </div>
    </div>
  );
}

function SlideCTA({ data, scale }) {
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <span style={{ fontSize: 16 * scale, fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: COL.accent, marginBottom: 24 * scale }}>
        End · 07 di 07
      </span>
      <h2 style={{ fontWeight: 900, fontSize: 140 * scale, lineHeight: 0.9, letterSpacing: "-0.03em", color: "#FFF", margin: 0 }}>
        {data.ctaLine1}<br />
        <span style={{ color: COL.accent }}>{data.ctaLine2}</span><br />
        {data.ctaLine3}
      </h2>
      <p style={{ fontSize: 28 * scale, color: COL.muted, lineHeight: 1.3, margin: `${32 * scale}px 0` }}>
        Salva questo post.<br />
        Prenota una consulenza gratuita:<br />
        <span style={{ color: "#FFF", fontWeight: 700 }}>leggiamo la tua polizza a voce alta.</span>
      </p>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: `${28 * scale}px ${40 * scale}px`, borderRadius: 16 * scale,
        background: COL.accent, color: COL.bg,
      }}>
        <span style={{ fontWeight: 900, fontSize: 52 * scale, letterSpacing: "-0.02em" }}>{data.phone}</span>
        <span style={{ fontSize: 16 * scale, fontFamily: "ui-monospace, monospace", fontWeight: 700, letterSpacing: "0.25em", textTransform: "uppercase" }}>Chiama →</span>
      </div>
      <p style={{ fontSize: 16 * scale, fontFamily: "ui-monospace, monospace", letterSpacing: "0.2em", textTransform: "uppercase", color: COL.muted, margin: `${20 * scale}px 0 0` }}>
        IVASS · A000464752 · Napoli · Portici
      </p>
    </div>
  );
}

const SLIDE_RENDERERS = {
  hook: SlideHook,
  barchart: SlideBarchart,
  kpi: SlideKPI,
  warning: SlideWarning,
  checklist: SlideChecklist,
  quote: SlideQuote,
  cta: SlideCTA,
};

const SLIDE_NAMES = {
  hook: "Hook",
  barchart: "Barchart",
  kpi: "3 KPI",
  warning: "Warning",
  checklist: "Checklist",
  quote: "Quote CEO",
  cta: "CTA",
};

// ===== DEFAULT DATA (modificabile) =====
const DEFAULT_DATA = {
  topic: "Furti casa Italia 2025",
  hook: {
    type: "hook",
    badge: "INSURANCE · ALERT",
    titleLines: [
      { text: "Furti in" },
      { text: "abitazione", italic: true },
      { text: "Italia 2025." },
    ],
    metricMain: "+18",
    metricUnit: "%",
    source: "vs anno precedente · fonte ANSA",
  },
  barchart: {
    type: "barchart",
    topTitle: "1 furto ogni",
    bigTitle: "4 minuti.",
    bars: [
      { label: "2021", value: 42 },
      { label: "2022", value: 48 },
      { label: "2023", value: 56 },
      { label: "2024", value: 65 },
      { label: "2025", value: 100, highlight: true, top: "156K" },
    ],
    source: "Furti denunciati/anno · ISTAT 2025",
  },
  kpi: {
    type: "kpi",
    kpis: [
      { value: "€3.800", label: "Danno medio", trend: "up", delta: "+22%" },
      { value: "32%", label: "Dei furti in Campania", trend: "flat" },
      { value: "41%", label: "Case senza polizza", trend: "up", delta: "+3pp" },
    ],
  },
  warning: {
    type: "warning",
    items: [
      { headline: "Casa vuota > 45 giorni", sub: "Polizza sospesa in vacanza lunga" },
      { headline: "Bici e monopattini fuori", sub: "Serve estensione dedicata" },
      { headline: "Contanti oltre €1.000", sub: "Limite standard contratto" },
    ],
  },
  checklist: {
    type: "checklist",
    checks: [
      "Massimale copre il VALORE reale dei beni?",
      "Obbligo di antifurto/casseforte chiaro?",
      "Bici, device, denaro contante inclusi?",
    ],
  },
  quote: {
    type: "quote",
    quoteLine1: "La polizza giusta non e' la piu' economica.",
    quoteLine2: "E' quella che paga al sinistro.",
    authorName: "Christian Colucci",
    authorRole: "CEO · Redazione · IVASS A000464752",
    authorPhoto: "https://www.colucciassicuratori.com/wp-content/uploads/2025/07/WhatsApp-Image-2025-07-11-at-18.11.52-1200x1500.jpeg",
  },
  cta: {
    type: "cta",
    ctaLine1: "Vuoi essere",
    ctaLine2: "davvero",
    ctaLine3: "coperto?",
    phone: "081 761 1000",
  },
  caption: `🚨 FURTI IN CASA · ITALIA 2025

+18% rispetto al 2024. 1 furto ogni 4 minuti.
Fonte: ANSA, ISTAT.

Nel carosello:
→ I 3 KPI che devi conoscere
→ Cosa la tua polizza NON copre davvero
→ 3 domande da fare prima del rinnovo
→ Il pensiero del nostro CEO

Salva il post e scrivici in DM per una consulenza gratuita.
IVASS · A000464752 · 📍 Napoli · Portici

#assicurazioni #polizzacasa #casasicura #furtoinabitazione #educazioneassicurativa #colucciassicuratori #napoli #portici`,
};

const SLIDE_KEYS = ["hook", "barchart", "kpi", "warning", "checklist", "quote", "cta"];

export default function CaroselloExportTab() {
  const [data, setData] = useState(DEFAULT_DATA);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [exports, setExports] = useState([]);
  const [previewIdx, setPreviewIdx] = useState(0);
  const exportRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/carosello-exports`, { withCredentials: true })
      .then(r => setExports(r.data || []))
      .catch(() => {});
  }, []);

  const updateSlide = (key, patch) => {
    setData(d => ({ ...d, [key]: { ...d[key], ...patch } }));
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(data.caption);
      toast.success("Didascalia copiata negli appunti");
    } catch {
      toast.error("Copia non riuscita (usa Ctrl+C manualmente)");
    }
  };

  const exportZip = async () => {
    if (!exportRef.current) return;
    setBusy(true);
    try {
      const zip = new JSZip();
      const slideNodes = exportRef.current.querySelectorAll("[data-slide-export]");
      if (slideNodes.length !== SLIDE_KEYS.length) {
        throw new Error("Slide non complete");
      }

      for (let i = 0; i < slideNodes.length; i++) {
        const key = SLIDE_KEYS[i];
        setProgress(`Rendering slide ${i + 1}/7 — ${SLIDE_NAMES[key]}...`);
        const canvas = await html2canvas(slideNodes[i], {
          backgroundColor: null,
          scale: 1,
          useCORS: true,
          allowTaint: false,
          logging: false,
          width: 1080,
          height: 1350,
        });
        const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.92));
        zip.file(`${String(i + 1).padStart(2, "0")}_${key}.jpg`, blob);
      }

      setProgress("Preparazione didascalia...");
      zip.file("didascalia.txt", data.caption || "");
      zip.file("istruzioni.txt",
`COME PUBBLICARE IL CAROSELLO SU INSTAGRAM:

1. Apri l'app "Meta Business Suite" (gratuita, Apple Store / Google Play)
   oppure il sito https://business.facebook.com
2. Tap su "Crea post" -> "Post Instagram"
3. Seleziona l'account @colucci_assicuratori
4. Carica tutte e 7 le immagini (01_hook.jpg ... 07_cta.jpg)
   IMPORTANTE: mantenere l'ordine numerico
5. Incolla la didascalia da "didascalia.txt" (e' gia' negli appunti)
6. Scegli: Pubblica ora OPPURE Schedula per data e ora future
7. Tap "Pubblica"

Tempo stimato: 30-60 secondi.

Suggerimenti:
- Programma 3-5 caroselli in una sessione per massimizzare l'efficienza
- Cross-posta anche su Facebook con lo stesso flusso (toggle "Pubblica anche su Facebook")
- Controlla insights dopo 24-48h per capire performance
`);

      const today = new Date().toISOString().slice(0, 10);
      const topicSlug = (data.topic || "carosello").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
      const filename = `colucci_${topicSlug}_${today}.zip`;
      setProgress("Creazione ZIP finale...");
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, filename);

      // Copy caption to clipboard
      try { await navigator.clipboard.writeText(data.caption); } catch {}

      // Save export record
      try {
        await axios.post(`${API}/admin/carosello-exports`, {
          topic: data.topic,
          filename,
          slides_count: SLIDE_KEYS.length,
          caption_length: (data.caption || "").length,
        }, { withCredentials: true });
        const newList = await axios.get(`${API}/admin/carosello-exports`, { withCredentials: true });
        setExports(newList.data || []);
      } catch {}

      toast.success("Carosello esportato! La didascalia e' negli appunti.", { duration: 6000 });
      setProgress("");
    } catch (e) {
      toast.error(`Errore: ${e.message || "export fallito"}`);
      setProgress("");
    }
    setBusy(false);
  };

  const renderPreviewSlide = (key, idx) => {
    const Renderer = SLIDE_RENDERERS[key];
    const slide = data[key];
    const light = key === "checklist";
    return (
      <ExportFrame key={key} index={idx} total={SLIDE_KEYS.length} scale={0.28} light={light}
        bg={light ? COL.light : COL.bg}>
        {(s) => <Renderer data={slide} scale={s} />}
      </ExportFrame>
    );
  };

  const renderExportSlide = (key, idx) => {
    const Renderer = SLIDE_RENDERERS[key];
    const slide = data[key];
    const light = key === "checklist";
    return (
      <div key={key} data-slide-export style={{ marginBottom: 20 }}>
        <ExportFrame index={idx} total={SLIDE_KEYS.length} scale={1} light={light}
          bg={light ? COL.light : COL.bg}>
          {(s) => <Renderer data={slide} scale={s} />}
        </ExportFrame>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="carosello-export-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#D97B58]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97B58]">Export Meta Business Suite</p>
        </div>
        <h2 className="font-heading text-2xl font-bold mb-1">Genera carosello e scarica ZIP</h2>
        <p className="text-sm text-white/60 max-w-2xl">
          Modifica titoli, dati e didascalia qui sotto. Clicca <strong>&quot;Esporta ZIP&quot;</strong>: ricevi 7 immagini 1080&times;1350 ottimizzate per Instagram + la caption gia&apos; negli appunti. Poi apri <strong>Meta Business Suite</strong> (app gratuita ufficiale di Meta) e incolla. 30-60 secondi.
        </p>
      </div>

      {/* Preview + navigation */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Topic del carosello</Label>
            <Input value={data.topic} onChange={e => setData(d => ({ ...d, topic: e.target.value }))}
              className="mt-1 max-w-md" data-testid="carosello-topic" />
          </div>
          <Button onClick={exportZip} disabled={busy}
            className="bg-[#D97B58] hover:bg-[#c86b4a] text-white font-semibold shadow-lg shrink-0"
            data-testid="export-zip-btn" size="lg">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sto esportando...</> : <><FileDown className="w-4 h-4 mr-2" /> Esporta ZIP per Instagram</>}
          </Button>
        </div>

        {busy && progress && (
          <div className="mb-4 p-3 rounded-lg bg-[#0F1F14] text-white text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-[#D97B58]" /> {progress}
          </div>
        )}

        {/* Preview grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5" data-testid="preview-grid">
          {SLIDE_KEYS.map((key, idx) => (
            <button key={key} onClick={() => setPreviewIdx(idx)}
              className={`relative rounded-lg overflow-hidden shadow-sm transition-all ${previewIdx === idx ? "ring-2 ring-[#D97B58] scale-[1.04]" : "opacity-70 hover:opacity-100"}`}
              data-testid={`preview-${key}`}>
              {renderPreviewSlide(key, idx)}
              <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-white/95 text-[8px] font-black text-zinc-900 uppercase tracking-wider">
                {idx + 1} · {SLIDE_NAMES[key]}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor per slide selezionata */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="slide-editor">
        <h3 className="font-heading text-lg font-bold text-zinc-900 mb-4">
          Modifica slide {previewIdx + 1} — {SLIDE_NAMES[SLIDE_KEYS[previewIdx]]}
        </h3>
        <SlideEditor slideKey={SLIDE_KEYS[previewIdx]} data={data[SLIDE_KEYS[previewIdx]]}
          onChange={(patch) => updateSlide(SLIDE_KEYS[previewIdx], patch)} />
      </div>

      {/* Caption editor */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-zinc-900">Didascalia Instagram</h3>
          <Button variant="outline" size="sm" onClick={copyCaption} data-testid="copy-caption">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copia
          </Button>
        </div>
        <Textarea rows={10} value={data.caption} onChange={e => setData(d => ({ ...d, caption: e.target.value }))}
          placeholder="Testo della didascalia con hashtag..." maxLength={2200} className="font-mono text-sm" data-testid="caption-textarea" />
        <p className="text-xs text-zinc-400 mt-1">{data.caption.length}/2200 caratteri · max 30 hashtag · verra&apos; copiata negli appunti al click su Esporta.</p>
      </div>

      {/* Istruzioni */}
      <div className="bg-gradient-to-br from-[#FFF8E6] to-[#FEDA77]/30 border border-[#E5C97B]/40 rounded-2xl p-6" data-testid="instructions">
        <div className="flex items-start gap-3">
          <Instagram className="w-6 h-6 text-[#0F1F14] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-zinc-900 mb-2">Dopo l&apos;export: 4 passaggi</h3>
            <ol className="space-y-1.5 text-sm text-zinc-700">
              <li><strong>1.</strong> Apri <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#0F1F14] underline font-semibold">Meta Business Suite</a> (o l&apos;app mobile)</li>
              <li><strong>2.</strong> &quot;Crea post&quot; → &quot;Post Instagram&quot; → account <code className="bg-white/80 px-1.5 py-0.5 rounded text-xs">@colucci_assicuratori</code></li>
              <li><strong>3.</strong> Trascina le 7 immagini dallo ZIP (ordine numerico 01→07) e incolla la didascalia (Ctrl+V / Cmd+V)</li>
              <li><strong>4.</strong> Clicca <strong>Pubblica ora</strong> o <strong>Programma</strong> per una data/ora futura</li>
            </ol>
            <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-4 h-9 rounded-full bg-[#0F1F14] text-white text-xs font-semibold hover:bg-[#1d4a2e] transition-colors" data-testid="open-business-suite">
              <ExternalLink className="w-3.5 h-3.5" /> Apri Meta Business Suite
            </a>
          </div>
        </div>
      </div>

      {/* Storico export */}
      {exports.length > 0 && (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="export-history">
          <h3 className="font-heading text-lg font-bold text-zinc-900 mb-4">Storico export</h3>
          <div className="space-y-2">
            {exports.slice(0, 20).map((e, i) => (
              <div key={e.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{e.topic}</p>
                  <p className="text-xs text-zinc-400 font-mono">{e.filename} · {new Date(e.created_at).toLocaleString("it-IT")}</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold shrink-0">{e.slides_count} slide</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hidden export container (full-size 1080x1350 per slide) */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}>
        <div ref={exportRef}>
          {SLIDE_KEYS.map((key, idx) => renderExportSlide(key, idx))}
        </div>
      </div>
    </div>
  );
}

// ==========================================================
// SlideEditor — campi dinamici per la slide attualmente selezionata
// ==========================================================
function SlideEditor({ slideKey, data, onChange }) {
  if (slideKey === "hook") return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2"><Label>Badge (in alto)</Label><Input value={data.badge} onChange={e => onChange({ badge: e.target.value })} /></div>
      {data.titleLines.map((ln, i) => (
        <div key={i}>
          <Label>Titolo riga {i + 1}{ln.italic ? " (italic)" : ""}</Label>
          <Input value={ln.text} onChange={e => {
            const newLines = [...data.titleLines];
            newLines[i] = { ...ln, text: e.target.value };
            onChange({ titleLines: newLines });
          }} />
        </div>
      ))}
      <div><Label>Metrica (numero)</Label><Input value={data.metricMain} onChange={e => onChange({ metricMain: e.target.value })} /></div>
      <div><Label>Unita&apos; (es. %)</Label><Input value={data.metricUnit} onChange={e => onChange({ metricUnit: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Fonte</Label><Input value={data.source} onChange={e => onChange({ source: e.target.value })} /></div>
    </div>
  );

  if (slideKey === "barchart") return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div><Label>Titolo piccolo</Label><Input value={data.topTitle} onChange={e => onChange({ topTitle: e.target.value })} /></div>
      <div><Label>Titolo grande (accent)</Label><Input value={data.bigTitle} onChange={e => onChange({ bigTitle: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Fonte</Label><Input value={data.source} onChange={e => onChange({ source: e.target.value })} /></div>
      <div className="md:col-span-2">
        <Label>Barre (label · valore 0-100 · top opzionale)</Label>
        {data.bars.map((b, i) => (
          <div key={i} className="grid grid-cols-[1fr,80px,1fr,auto] gap-2 mt-1">
            <Input value={b.label} onChange={e => { const nb = [...data.bars]; nb[i] = { ...b, label: e.target.value }; onChange({ bars: nb }); }} />
            <Input type="number" min={0} max={100} value={b.value} onChange={e => { const nb = [...data.bars]; nb[i] = { ...b, value: parseInt(e.target.value) || 0 }; onChange({ bars: nb }); }} />
            <Input value={b.top || ""} placeholder="es. 156K" onChange={e => { const nb = [...data.bars]; nb[i] = { ...b, top: e.target.value }; onChange({ bars: nb }); }} />
            <label className="flex items-center gap-1 text-xs px-2">
              <input type="checkbox" checked={!!b.highlight} onChange={e => { const nb = [...data.bars]; nb[i] = { ...b, highlight: e.target.checked }; onChange({ bars: nb }); }} /> Accent
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  if (slideKey === "kpi") return (
    <div>
      {data.kpis.map((k, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr,2fr,120px,120px] gap-2 mb-2">
          <Input value={k.value} placeholder="€3.800" onChange={e => { const nk = [...data.kpis]; nk[i] = { ...k, value: e.target.value }; onChange({ kpis: nk }); }} />
          <Input value={k.label} placeholder="Danno medio" onChange={e => { const nk = [...data.kpis]; nk[i] = { ...k, label: e.target.value }; onChange({ kpis: nk }); }} />
          <select value={k.trend} onChange={e => { const nk = [...data.kpis]; nk[i] = { ...k, trend: e.target.value }; onChange({ kpis: nk }); }} className="border rounded px-2 text-sm">
            <option value="up">↑ up</option><option value="flat">— flat</option>
          </select>
          <Input value={k.delta || ""} placeholder="+22%" onChange={e => { const nk = [...data.kpis]; nk[i] = { ...k, delta: e.target.value }; onChange({ kpis: nk }); }} />
        </div>
      ))}
    </div>
  );

  if (slideKey === "warning") return (
    <div>
      {data.items.map((it, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <Input value={it.headline} placeholder="Titolo warning" onChange={e => { const ni = [...data.items]; ni[i] = { ...it, headline: e.target.value }; onChange({ items: ni }); }} />
          <Input value={it.sub} placeholder="Dettaglio" onChange={e => { const ni = [...data.items]; ni[i] = { ...it, sub: e.target.value }; onChange({ items: ni }); }} />
        </div>
      ))}
    </div>
  );

  if (slideKey === "checklist") return (
    <div className="space-y-2">
      {data.checks.map((c, i) => (
        <Input key={i} value={c} onChange={e => { const nc = [...data.checks]; nc[i] = e.target.value; onChange({ checks: nc }); }} />
      ))}
    </div>
  );

  if (slideKey === "quote") return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2"><Label>Citazione riga 1 (bianco)</Label><Input value={data.quoteLine1} onChange={e => onChange({ quoteLine1: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Citazione riga 2 (accent ocra)</Label><Input value={data.quoteLine2} onChange={e => onChange({ quoteLine2: e.target.value })} /></div>
      <div><Label>Nome autore</Label><Input value={data.authorName} onChange={e => onChange({ authorName: e.target.value })} /></div>
      <div><Label>Ruolo autore</Label><Input value={data.authorRole} onChange={e => onChange({ authorRole: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>URL foto autore</Label><Input value={data.authorPhoto} onChange={e => onChange({ authorPhoto: e.target.value })} /></div>
    </div>
  );

  if (slideKey === "cta") return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div><Label>Riga 1</Label><Input value={data.ctaLine1} onChange={e => onChange({ ctaLine1: e.target.value })} /></div>
      <div><Label>Riga 2 (accent)</Label><Input value={data.ctaLine2} onChange={e => onChange({ ctaLine2: e.target.value })} /></div>
      <div><Label>Riga 3</Label><Input value={data.ctaLine3} onChange={e => onChange({ ctaLine3: e.target.value })} /></div>
      <div><Label>Numero di telefono</Label><Input value={data.phone} onChange={e => onChange({ phone: e.target.value })} /></div>
    </div>
  );

  return null;
}
